// eslint-disable-next-line no-unused-vars
import React, { memo, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useFetch from 'Hooks/useFetch';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import PasswordChangeModalComponent from 'Components/PasswordChange/PasswordChangeModal';
import { API_ACCOUNT } from 'Constants';

const languages = [
  { label: 'RO', value: 'ro' },
  { label: 'EN', value: 'en' }
];

const defaultValues = {
  password: '',
  confirmPassword: '',
};

const PasswordChangeModal = memo(() => {
  const [loading, setLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [errors, setErrors] = useState([]);
  const { params, push } = useRouter();
  const { data: user, setUserData } = useUser();
  const { t } = useTranslation();
  const fetcher = useFetch();
  const {
    formState: { errors: rhfErrors },
    getValues,
    register,
    watch,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const passwordValue = watch('password');

  const validators = useMemo(() => ({
    password: {
      required: () => t('errors.fieldBlank'),
      pattern: {
        value: /[^\w\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))/,
        message: t('profile.passwordValidation'),
      }
    },
    confirmPassword: {
      required: () => t('errors.fieldBlank'),
      validate: {
        match: value => value === passwordValue || t('profile.passwordMatch'),
      }
    }
  }), [passwordValue]);

  const handleSubmit = async () => {
    setLoading(true);
    trigger();

    const { password: first, confirmPassword: second } = getValues();

    if (first !== second || Object.keys(rhfErrors).length) {
      setErrors([{ message: t('profile.passwordMatch'), type: 'error' }]);
      setLoading(false);
      return;
    }

    fetcher(`${API_ACCOUNT}/finalize-password-recovery/${ params.id }`, {
      headers: new Headers({ 'Accept-Language': user?.language }),
      method: 'POST',
      body: JSON.stringify({
        password: {
          first,
          second,
        }
      }),
    }).then(() => {
      setPasswordChanged(true);
      setTimeout(() => {
        push('/login');
      }, 5000);
    }).catch(err => {
      setErrors([{ message: err?.info?.errors[0]?.message || t('errors.genericErrorMessage'), type: 'error' }]);
    }).finally(() => setLoading(false));
  };

  return (
    <PasswordChangeModalComponent
      register={ register }
      loading={ loading }
      onSubmit={ handleSubmit }
      errors={ errors }
      rhfErrors={ rhfErrors }
      languages={ languages }
      language={ languages.find(lang => lang.value === user?.language) }
      setLanguage={ lang => setUserData({ language: lang.value }) }
      validators={ validators }
      passwordChanged={ passwordChanged }
    />
  );
});

PasswordChangeModal.displayName = 'PasswordChangeModal';

export default PasswordChangeModal;
